// features/preApprovals/preApprovalSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchPreApprovalLogs,
  PreApprovalLog,
  PreApprovalLogFilters,
} from '../../services/preApprovalLogs';

interface PreApprovalState {
  logs: PreApprovalLog[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: PreApprovalLogFilters;
  hasFetched: boolean;
  error?: string;
}

const initialState: PreApprovalState = {
  logs: [],
  total: 0,
  loading: false,
  page: 1,
  pageSize: 10,
  searchQuery: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  filters: {
    platform: '',
    lender: '',
    dateRange: { from: undefined, to: undefined },
    partner: '',
  },
  hasFetched: false,
};

// --- Async thunk without generics ---
export const fetchPreApprovalsThunk = createAsyncThunk(
  'preApproval/fetchPreApprovals',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Manually cast getState
      const state = getState() as { preApproval: PreApprovalState; auth: { token: string } };
      const { page, pageSize, searchQuery, sortBy, sortOrder, filters } = state.preApproval;
      const { token } = state.auth;

      const response = await fetchPreApprovalLogs({
        page,
        pageSize,
        sortField: sortBy,
        sortOrder,
        search: searchQuery,
        token,
        filters,
      });

      return {
        logs: response.logs,
        total: response.totalMatch,
        page: response.pageNo,
        pageSize: response.pageSize,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const preApprovalSlice = createSlice({
  name: 'preApproval',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setFilters: (state, action: PayloadAction<PreApprovalLogFilters>) => {
      state.filters = action.payload;
      state.page = 1; // reset page on filter change
    },
    resetFilters: (state) => {
      state.filters = {
        platform: '',
        lender: '',
        dateRange: { from: undefined, to: undefined },
        partner: '',
      };
      state.page = 1;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreApprovalsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchPreApprovalsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.logs;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.hasFetched = true;
      })
      .addCase(fetchPreApprovalsThunk.rejected, (state, action) => {
        state.loading = false;
        state.logs = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setFilters,
  resetFilters,
  setHasFetched,
} = preApprovalSlice.actions;

export default preApprovalSlice.reducer;
